import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { recordPageview } from '@coparent/shared/src/services/analyticsService';

const PUBLIC_PATHS = new Set(['/', '/blog', '/privacy', '/terms']);

function isPublicPath(pathname) {
  if (PUBLIC_PATHS.has(pathname)) return true;
  if (pathname.startsWith('/blog/')) return true;
  return false;
}

// Minimal bot filter. A determined actor can still forge impressions since the
// write is unauthenticated, but this cuts out the easy 90% — headless Chrome,
// Selenium, most scrapers.
function looksLikeBot() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.webdriver) return true;
  const ua = (navigator.userAgent || '').toLowerCase();
  return /(bot|crawler|spider|headless|phantomjs|puppeteer|playwright)/.test(ua);
}

const storage = {
  get: async (key) => {
    try {
      return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
    } catch {
      return null;
    }
  },
  set: async (key, value) => {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value);
    } catch {
      // Safari private mode, etc. — not a dealbreaker.
    }
  },
};

// Strip same-origin referrers — they're internal navigation and would flood
// the 'referral' bucket with our own URLs.
function externalReferrer() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return '';
  const ref = document.referrer || '';
  if (!ref) return '';
  try {
    const refOrigin = new URL(ref).origin;
    if (refOrigin === window.location.origin) return '';
  } catch {
    // Malformed referrer — treat as external, let the source classifier handle it.
  }
  return ref;
}

export default function usePageviewPing() {
  const location = useLocation();

  useEffect(() => {
    if (!isPublicPath(location.pathname)) return;
    if (looksLikeBot()) return;
    recordPageview({
      path: location.pathname,
      search: location.search,
      referrer: externalReferrer(),
      storage,
    });
  }, [location.pathname, location.search]);
}
